import React, { useState, useEffect, useMemo } from "react";

// ═══════════════════════════════════════════════════════════════
//  YOURS TRULY — Purple Spirit Theme
//  Rolls Royce Spirit of Ecstasy design system
//  Import: import { THEME, SERIF, SANS, PLAY, Starfield, ThemeToggle, useTheme, PageWrap } from "./theme";
// ═══════════════════════════════════════════════════════════════

// ── Typography ──
export var SERIF = '"Cormorant Garamond", Georgia, serif';
export var SANS  = '"Outfit", Helvetica, sans-serif';
export var PLAY  = '"Playfair Display", Georgia, serif';

// ── Palette ──
export var THEME = {
  dark: {
    bg:         "#0E0B18",
    grad:       "radial-gradient(ellipse at 50% 20%, rgba(30,22,50,0.6) 0%, transparent 60%), #0E0B18",
    text:       "#E8E6E2",
    textSoft:   "rgba(232,230,226,0.52)",
    textMuted:  "rgba(232,230,226,0.32)",
    accent:     "#B8967A",
    accentSoft: "rgba(184,150,122,0.55)",
    accentMuted:"rgba(184,150,122,0.22)",
    purple:     "rgba(120,90,180,0.12)",
    purpleSoft: "rgba(120,90,180,0.06)",
    border:     "rgba(232,230,226,0.10)",
    cardBg:     "rgba(232,230,226,0.04)",
    inputBg:    "rgba(232,230,226,0.06)",
    btnBg:      "#E8E6E2",
    btnText:    "#0E0B18",
    btnSecBg:   "transparent",
    btnSecText: "#E8E6E2",
    btnSecBorder:"rgba(232,230,226,0.18)",
  },
  light: {
    bg:         "#F8F5F0",
    grad:       "radial-gradient(ellipse at 50% 20%, rgba(184,150,122,0.04) 0%, transparent 60%), #F8F5F0",
    text:       "#1A1720",
    textSoft:   "rgba(26,23,32,0.52)",
    textMuted:  "rgba(26,23,32,0.32)",
    accent:     "#8B6F58",
    accentSoft: "rgba(139,111,88,0.55)",
    accentMuted:"rgba(139,111,88,0.22)",
    purple:     "rgba(80,60,120,0.06)",
    purpleSoft: "rgba(80,60,120,0.03)",
    border:     "rgba(26,23,32,0.10)",
    cardBg:     "rgba(26,23,32,0.03)",
    inputBg:    "rgba(26,23,32,0.04)",
    btnBg:      "#1A1720",
    btnText:    "#F8F5F0",
    btnSecBg:   "transparent",
    btnSecText: "#1A1720",
    btnSecBorder:"rgba(26,23,32,0.18)",
  },
};

// ── Font injection (call once per page) ──
export function injectFonts() {
  if (typeof document === "undefined") return;
  if (document.getElementById("yt-fonts")) return;
  var link = document.createElement("link");
  link.id = "yt-fonts";
  link.rel = "stylesheet";
  link.href = "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400&family=Outfit:wght@200;300;400;500;600&family=Playfair+Display:ital,wght@0,400;0,500;1,400&display=swap";
  document.head.appendChild(link);
}

// ── Theme hook ──
export function useTheme() {
  var [mode, setMode] = useState(function() {
    try { return localStorage.getItem("yt_theme") || "dark"; } catch(e) { return "dark"; }
  });
  var t = THEME[mode];

  useEffect(function() {
    try { localStorage.setItem("yt_theme", mode); } catch(e) {}
    document.body.style.background = t.bg;
    document.body.style.margin = "0";
    document.body.style.transition = "background 0.6s";
    injectFonts();
  }, [mode, t.bg]);

  var toggle = function() { setMode(mode === "dark" ? "light" : "dark"); };
  return { mode: mode, t: t, toggle: toggle };
}

// ── Theme toggle button ──
export function ThemeToggle(props) {
  var t = props.t;
  var toggle = props.toggle;
  var mode = props.mode;
  return React.createElement("button", {
    onClick: toggle,
    style: {
      position: "fixed", top: 28, right: 32, zIndex: 100,
      background: "none", border: "1px solid " + t.border,
      padding: "8px 18px", cursor: "pointer",
      fontFamily: SANS, fontSize: 9, fontWeight: 300,
      letterSpacing: 4, color: t.textMuted, textTransform: "uppercase",
    }
  }, mode === "dark" ? "light" : "dark");
}

// ═══════════════════════════════════════════════════════
//  STARFIELD — Rolls Royce starlight headliner effect
// ═══════════════════════════════════════════════════════

var STAR_CSS_INJECTED = false;
function injectStarCSS() {
  if (STAR_CSS_INJECTED || typeof document === "undefined") return;
  STAR_CSS_INJECTED = true;
  var s = document.createElement("style");
  s.textContent = [
    "@keyframes ytStar1{0%,100%{opacity:0.15}50%{opacity:0.8}}",
    "@keyframes ytStar2{0%,100%{opacity:0.1}40%{opacity:0.6}80%{opacity:0.2}}",
    "@keyframes ytStar3{0%,100%{opacity:0.05}30%{opacity:0.45}70%{opacity:0.15}}",
    "@keyframes ytStarBright{0%,100%{opacity:0.3;transform:scale(1)}50%{opacity:1;transform:scale(1.8)}}",
  ].join("\n");
  document.head.appendChild(s);
}

function makeStars(count) {
  var stars = [];
  for (var i = 0; i < count; i++) {
    var isBright = Math.random() < 0.06;
    var isGold = Math.random() < 0.12;
    stars.push({
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: isBright ? 2 + Math.random() * 1.5 : 0.5 + Math.random() * 1.8,
      color: isGold ? "rgba(184,150,122," : "rgba(232,230,226,",
      anim: isBright ? "ytStarBright" : "ytStar" + (1 + Math.floor(Math.random() * 3)),
      dur: 3 + Math.random() * 8,
      delay: Math.random() * 12,
      baseOp: isBright ? 0.3 : 0.05 + Math.random() * 0.2,
    });
  }
  return stars;
}

export function Starfield(props) {
  var count = (props && props.count) || 120;
  var fixed = !(props && props.fixed === false);
  var stars = useMemo(function() { return makeStars(count); }, [count]);

  useEffect(function() { injectStarCSS(); }, []);

  return React.createElement("div", {
    style: {
      position: fixed ? "fixed" : "absolute",
      top: 0, left: 0, width: "100%", height: "100%",
      pointerEvents: "none", zIndex: 0, overflow: "hidden",
    },
    "aria-hidden": "true",
  }, stars.map(function(s, i) {
    return React.createElement("div", {
      key: i,
      style: {
        position: "absolute",
        left: s.x + "%", top: s.y + "%",
        width: s.size, height: s.size,
        borderRadius: "50%",
        background: s.color + s.baseOp + ")",
        animation: s.anim + " " + s.dur + "s ease-in-out " + s.delay + "s infinite",
      }
    });
  }));
}

// ── Page wrapper ──
export function PageWrap(props) {
  var t = props.t;
  return React.createElement("div", {
    style: {
      position: "relative", minHeight: "100vh",
      background: t.grad, fontFamily: SANS,
      color: t.text, transition: "color 0.6s, background 0.6s",
      isolation: "isolate",
    }
  },
    !props.noStars && React.createElement(Starfield, null),
    React.createElement("div", { style: { position: "relative", zIndex: 1 } }, props.children)
  );
}

// ── Nav bar ──
export function Nav(props) {
  var t = props.t;
  return React.createElement("nav", {
    style: {
      display: "flex", justifyContent: "space-between", alignItems: "center",
      padding: "36px 48px", maxWidth: 1200, margin: "0 auto",
    }
  },
    React.createElement("a", {
      href: "/crystal",
      style: { fontFamily: SANS, fontSize: 9, fontWeight: 400, letterSpacing: 8, color: t.textSoft, textTransform: "uppercase", textDecoration: "none" }
    }, "Yours Truly"),
    React.createElement("a", {
      href: "/new-constellation",
      style: { fontFamily: SANS, fontSize: 9, fontWeight: 400, color: t.textSoft, textDecoration: "none", letterSpacing: 4, textTransform: "uppercase" }
    }, "Begin")
  );
}

// ── Flowing SVG line divider ──
export function LineDivider(props) {
  var t = props.t;
  var q = props.flip ? 35 : 5;
  return React.createElement("div", {
    style: { maxWidth: 1200, margin: "0 auto", padding: "0 48px", overflow: "hidden" }
  }, React.createElement("svg", { viewBox: "0 0 1200 40", style: { width: "100%", height: 40, display: "block" } },
    React.createElement("path", { d: "M0 20 Q150 "+q+" 300 20 T600 20 T900 20 T1200 20", fill: "none", stroke: t.accentMuted, strokeWidth: "1" }),
    React.createElement("path", { d: "M0 22 Q150 "+(q+3)+" 300 22 T600 22 T900 22 T1200 22", fill: "none", stroke: t.accentMuted, strokeWidth: "0.5", opacity: ".5" })
  ));
}

// ── Reusable style helpers ──
export function btnStyle(t, variant) {
  var b = {
    display: "inline-block", padding: "18px 48px", borderRadius: 0,
    fontSize: 10, fontWeight: 500, fontFamily: SANS, letterSpacing: 4,
    textDecoration: "none", textTransform: "uppercase", cursor: "pointer",
    border: "none", transition: "all 0.4s cubic-bezier(0.25,0.46,0.45,0.94)",
  };
  if (variant === "secondary") {
    b.background = t.btnSecBg; b.color = t.btnSecText; b.border = "1px solid " + t.btnSecBorder;
  } else {
    b.background = t.btnBg; b.color = t.btnText;
  }
  return b;
}
export function inputStyle(t) {
  return {
    width: "100%", padding: "16px 20px", borderRadius: 0,
    border: "1px solid " + t.border, background: t.inputBg,
    color: t.text, fontFamily: SANS, fontSize: 16, fontWeight: 300,
    outline: "none", transition: "border-color 0.3s", boxSizing: "border-box",
  };
}
export function labelStyle(t) {
  return {
    fontFamily: SANS, fontSize: 9, fontWeight: 300, letterSpacing: 4,
    color: t.accentSoft, textTransform: "uppercase", marginBottom: 10, display: "block",
  };
}
